import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Alert, Box, Button, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import Typography from "@mui/material/Typography";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import ServiceRatesTab from "./ServiceRatesTab";
import ServiceDetailsForm from "./ServiceDetailsForm";
import ServiceBookingEnginesTab from "./ServiceBookingEnginesTab";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useQuery, useQueryClient} from "react-query";
import {useListAgendas} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import ConfirmationSettingsSection from "../newsletterAccount/ConfirmationSettingsSection";
import ConnectCustomDomainDialog from "../newsletterAccount/ConnectCustomDomainDialog";
import CreateTransactionalMailTemplateDialog from "../newsletterAccount/CreateTransactionalMailTemplateDialog";
import {CustomDomainService} from "crqlar-react-common/dist/services/newsletter/customDomain.service";
import {AgendaService} from "crqlar-react-common/dist/services/agenda/agenda.service";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {isServiceValid, prepareServiceForSave} from "src/utils/serviceSave.util";

const ServiceDetails = () => {

    useTrackScreenVisit('system service details')

    const {id} = useParams()
    const navigate = useNavigate()
    const {t} = useLocalize()
    const queryClient = useQueryClient()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'content', 'rates', 'bookingEngine', 'emails'], 'basic')
    const setActiveTab = (newTab: string) => setSearchParams({tab: newTab})

    const [showBufferTimeStatus, setShowBufferTimeStatus] = useState(false)
    const [selectedAgendas, setSelectedAgendas] = useState<AgendaV3[]>([])
    const [customDomainDialogOpen, setCustomDomainDialogOpen] = useState(false)
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false)

    const customDomainQ = useQuery(
        ['customDomain'],
        () => CustomDomainService.get(),
        {staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false}
    )

    const agendasQuery = useListAgendas()
    const agendas = useMemo(() => agendasQuery.data ?? [], [agendasQuery.data])
    const originalAgendasRef = useRef<AgendaV3[] | null>(null)

    // Why the backend refused to save the tip service (its rule: not bookable, staff only, one default
    // tip service per sector, any number linked to staff) - beside the switch, until the service changes
    const [tipServiceRefusal, setTipServiceRefusal] = useState<string>()
    const saveService = async (save: () => Promise<Service>, service: Service) => {
        setTipServiceRefusal(undefined)
        try {
            return await save()
        } catch (e: any) {
            const message = e?.response?.data?.message
            if (service.tipService && typeof message === 'string' && message.trim()) setTipServiceRefusal(message.trim())
            throw e
        }
    }

    const serviceData = useEntity({
        get: async () => {
            if (id && id !== 'new') return ServiceService.getServiceById(id)
            return {}
        },
        create: service => saveService(() => ServiceService.createService(service), service),
        update: async (service) => {
            if (selectedAgendas.length > 0 && originalAgendasRef.current) {
                const modsToUpdate = getModifiedAgendas(selectedAgendas, originalAgendasRef.current, service.id!)
                if (modsToUpdate.length > 0) {
                    await Promise.all(modsToUpdate.map(a => AgendaService.updateAgenda(a)))
                }
            }
            return saveService(() => ServiceService.updateService(service), service)
        },
        delete: (id) => ServiceService.deleteService({id: [id]}),
        onDeleted: () => {
            navigate('/system/services', {replace: true})
        },
        onSaved: (service) => {
            if (id === 'new') navigate(`/system/services/${service.id}`, {replace: true})
            else {
                queryClient.invalidateQueries('allServices', {exact: false})
                queryClient.invalidateQueries('agenda', {exact: false})
            }
            if (service.resourceCategoryDurationMinutes) setShowBufferTimeStatus(true)
            agendasQuery.refetch()
            resetAgendasState()
        },
        isValid: isServiceValid,
        cleanupBeforeSave: prepareServiceForSave
    }, id)

    const service = serviceData.item
    const disabled = !!service.managedExternally

    // What the tip services' rule looks at changed: the refusal is behind
    const tipRuleKey = [service.tipService, service.status, service.sectorIds?.join(), service.resourceIds?.join()].join('|')
    useEffect(() => {
        setTipServiceRefusal(undefined)
    }, [tipRuleKey])

    const primarySector = useSector(service.sectorIds?.[0])
    const setService = serviceData.setItem

    const updateField = (field: keyof Service, value: any) => {
        setService({...service, [field]: value})
    }

    useEffect(() => {
        if (service?.id && agendas.length > 0) {
            const current = agendas.filter(a => a.serviceIds?.includes(service.id!))
            setSelectedAgendas(current)
            if (!originalAgendasRef.current) originalAgendasRef.current = [...current]
        }
    }, [service?.id, agendas])

    const [agendaChanged, setAgendaChanged] = useState(false)

    const updateAgendas = (newSelected: AgendaV3[]) => {
        setSelectedAgendas(newSelected)
        const hasChanged = selectedAgendas.length !== newSelected.length ||
            !selectedAgendas.every(a => newSelected.some(n => n.id === a.id))
        setAgendaChanged(hasChanged)
    }

    const resetAgendasState = () => {
        const current = agendas.filter(a => a.serviceIds?.includes(service.id!))
        setSelectedAgendas(current)
        originalAgendasRef.current = [...current]
        setAgendaChanged(false)
    }

    const getModifiedAgendas = (selected: AgendaV3[], original: AgendaV3[], serviceId: string): AgendaV3[] => {
        const updated: AgendaV3[] = []
        const selectedIds = new Set(selected.map(a => a.id))
        const originalIds = new Set(original.map(a => a.id))

        original.forEach(agenda => {
            if (!selectedIds.has(agenda.id)) {
                updated.push({...agenda, serviceIds: (agenda.serviceIds ?? []).filter(id => id !== serviceId)})
            }
        })
        selected.forEach(agenda => {
            if (!originalIds.has(agenda.id)) {
                const serviceIds = agenda.serviceIds ?? []
                if (!serviceIds.includes(serviceId)) updated.push({...agenda, serviceIds: [...serviceIds, serviceId]})
            }
        })
        return updated
    }

    const headerSection = (
        <SettingsEntityHeader
            title={service.name}
            data={serviceData}
            back={() => navigate('/system/services')}
            restrictEdit={disabled}
            forceEnableSave={agendaChanged}
        />
    )

    const titleSection = disabled ? (
        <Stack gap={2}>
            <Alert
                variant={'outlined'}
                severity={'warning'}
                sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
            >
                {t('This entity is managed by an external system and thus cannot be edited here.')}
            </Alert>
        </Stack>
    ) : null

    const tabsSection = (
        <Box sx={{mb: 4}}>
            <TabContext value={activeTab}>
                <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                    <Tab label={t('Basic info')} value={'basic'}/>
                    <Tab label={t('Content & Translations')} value={'content'}/>
                    <Tab label={t('Rates')} value={'rates'} disabled={!service.id}/>
                    <Tab label={t('Booking engine')} value={'bookingEngine'} disabled={!service.id}/>
                    <Tab label={t('Reservation Emails')} value={'emails'} disabled={!service.id}/>
                </ScrollableTabList>
            </TabContext>
        </Box>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete service')}
                description={t('This permanently deletes the service. Existing reservations using it may stop working correctly. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={serviceData.saving}
                onDelete={() => serviceData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {service.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    if (serviceData.loading) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                {headerSection}
                <DetailsLoading/>
            </Box>
        )
    }

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    gap: 2,
                }}
            >
                {titleSection}
                {tabsSection}
            </Stack>

            {activeTab === 'basic' && (
                <>
                    <ServiceDetailsForm
                        serviceData={serviceData}
                        updateField={updateField}
                        showBufferTimeStatus={showBufferTimeStatus}
                        agendas={agendas}
                        selectedAgendas={selectedAgendas}
                        updateAgendas={updateAgendas}
                        tipServiceRefusal={tipServiceRefusal}
                    />

                    {!!service.id && (
                        <ScreenWrapper>
                            <Stack
                                sx={{
                                    width: '100%',
                                    maxWidth: '900px',
                                    alignSelf: 'center',
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    pt: 6,
                                }}
                            >
                                {dangerZoneSection}
                            </Stack>
                        </ScreenWrapper>
                    )}
                </>
            )}

            {activeTab === 'content' && (
                <ServiceDetailsForm
                    serviceData={serviceData}
                    updateField={updateField}
                    showBufferTimeStatus={showBufferTimeStatus}
                    agendas={agendas}
                    selectedAgendas={selectedAgendas}
                    updateAgendas={updateAgendas}
                    section={'content'}
                />
            )}

            {activeTab === 'rates' && (
                <ServiceRatesTab
                    service={service}
                    updateField={updateField}
                    disabled={disabled}
                />
            )}

            {activeTab === 'bookingEngine' && service.id && (
                <ServiceBookingEnginesTab
                    serviceId={service.id}
                    disabled={disabled}
                />
            )}

            {activeTab === 'emails' && (
                <ScreenWrapper>
                    <Stack
                        sx={{
                            width: '100%',
                            maxWidth: '900px',
                            gap: 2,
                            alignSelf: 'center',
                        }}
                    >
                        <Stack direction={'row'} justifyContent={'flex-end'} gap={2} mb={2}>
                            <Button
                                variant={'contained'}
                                onClick={() => setCustomDomainDialogOpen(true)}
                            >
                                {customDomainQ.data ? t('Connected domain') : t('Connect custom sending domain')}
                            </Button>
                            <Button
                                variant={'contained'}
                                onClick={() => setTemplateDialogOpen(true)}
                            >
                                {t('Create Template')}
                            </Button>
                        </Stack>
                        <ConnectCustomDomainDialog
                            open={customDomainDialogOpen}
                            onClose={() => setCustomDomainDialogOpen(false)}
                            existingDomain={customDomainQ.data ?? null}
                            onDomainSaved={() => customDomainQ.refetch()}
                        />
                        <CreateTransactionalMailTemplateDialog
                            open={templateDialogOpen}
                            onClose={() => setTemplateDialogOpen(false)}
                            onSaved={() => {
                                setTemplateDialogOpen(false);
                                queryClient.invalidateQueries(['transactionalMailTemplates']);
                            }}
                        />
                        <ConfirmationSettingsSection
                            confirmationSettings={{
                                ...primarySector?.notificationSettings?.confirmationSettings,
                                ...service?.notificationSettings?.confirmationSettings
                            }}
                            onSettingChange={(event, value) => {
                                updateField('notificationSettings', {
                                    ...service?.notificationSettings,
                                    confirmationSettings: {
                                        ...service?.notificationSettings?.confirmationSettings,
                                        [event]: value
                                    }
                                })
                            }}
                            gap={2}
                        />
                    </Stack>
                </ScreenWrapper>
            )}
        </Box>
    )
}

export default ServiceDetails
